<template>
  <div v-if="showDialog" class="privacy-consent-overlay">
    <div class="privacy-consent-dialog">
      <!-- 第一步：阅读并同意协议 -->
      <template v-if="step === 'consent'">
        <div class="dialog-title">温馨提示</div>

        <div class="dialog-content">
          <p>欢迎使用春信精油！我们非常重视您的个人信息和隐私保护。</p>
          <p>
            在您使用本应用前，请仔细阅读并充分理解
            <span class="policy-link" @click="goToAgreement">《用户协议》</span>和
            <span class="policy-link" @click="goToPolicy">《隐私政策》</span>，
            以了解我们如何收集、使用、存储与保护您的个人信息，以及您享有的相关权利。
          </p>
          <p>
            未经您同意，我们不会收集、传输您的任何个人信息；当您点击"同意"后使用登录、下单、
            语音通话、消息、位置等功能时，我们才会按需申请相机、麦克风、相册、位置等系统权限，
            仅用于实现对应功能。
          </p>
          <p>若您不同意上述协议内容，请点击"拒绝"，我们将无法为您提供相应服务。</p>
        </div>

        <div class="dialog-buttons">
          <button class="btn btn-secondary" @click="step = 'confirmExit'">拒绝</button>
          <button class="btn btn-agree" @click="handleAgree">同意</button>
        </div>
      </template>

      <!-- 第二步：拒绝确认（内嵌在本弹窗内，不依赖外部弹层，各工程行为一致） -->
      <template v-else>
        <div class="dialog-title">温馨提示</div>

        <div class="dialog-content">
          <p>为更好地保障您的合法权益，请阅读并同意《用户协议》和《隐私政策》。</p>
          <p>拒绝后将无法使用春信精油，确定退出应用吗？</p>
        </div>

        <div class="dialog-buttons">
          <button class="btn btn-secondary" @click="step = 'consent'">继续阅读</button>
          <button class="btn btn-agree" @click="handleExit">退出应用</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { App } from '@capacitor/app'
import { isNativeApp, isIOS } from '@/utils/environment'
import { SystemPlugin } from '@/plugins/system'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['agree'])

const router = useRouter()
const route = useRoute()

// 点击弹窗里的协议/政策链接进入 /corp/ 页面时，暂时隐藏弹窗避免盖住内容；返回后自动恢复
const showDialog = computed(() => props.show && !route.path.startsWith('/corp/'))

// 弹窗内两步状态：consent-阅读同意；confirmExit-拒绝后二次确认
const step = ref('consent')

const goToPolicy = () => {
  // 注意：真正的隐私政策是 /corp/policy(Policy.vue)；
  // /corp/privacy-protection 是"号码保护说明"，别指过去
  router.push('/corp/policy')
}

const goToAgreement = () => {
  router.push('/corp/agreement')
}

const handleAgree = () => {
  emit('agree')
}

const handleExit = () => {
  if (isNativeApp()) {
    if (isIOS()) {
      // @capacitor/app 的 exitApp 仅 Android 实现，iOS 上是 no-op，走壳工程 SystemPlugin
      SystemPlugin.exitApp().catch((error) => {
        console.error('退出 App 失败:', error)
      })
    } else {
      App.exitApp().catch((error) => {
        console.error('退出 App 失败:', error)
      })
    }
  } else {
    // H5/浏览器环境无法真正退出（window.close 受限），停留在确认界面即可
    window.close()
  }
}
</script>

<style scoped>
.privacy-consent-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
}

.privacy-consent-dialog {
  width: 100%;
  max-width: 340px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  background: #FFFFFF;
  border-radius: 16px;
  overflow: hidden;
}

.dialog-title {
  padding: 20px 24px 8px;
  font-size: 17px;
  font-weight: 600;
  color: #1A1A1A;
  text-align: center;
}

.dialog-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 20px 12px;
  font-size: 13px;
  color: #666666;
  line-height: 1.7;
}

.dialog-content p {
  margin: 0 0 8px 0;
}

.policy-link {
  color: #0EA5E9;
  font-weight: 500;
}

.dialog-buttons {
  display: flex;
  gap: 10px;
  padding: 6px;
}

.btn {
  flex: 1;
  height: 38px;
  font-size: 14px;
  border: none;
  border-radius: 8px;
  background: #F5F6F8;
  color: #666666;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:active {
  transform: scale(0.98);
}

.btn-secondary {
  background: #F5F6F8;
  color: #666666;
}

.btn-agree {
  color: #FFFFFF;
  background: linear-gradient(90deg, #ff6309 0%, #ff5757 100%);
  font-weight: 600;
}
</style>
